
import React from 'react';

const GameMissions = () => {
  const missions = [
    {
      id: 1,
      title: "Training Facility",
      difficulty: "Tutorial",
      objectives: [
        "Complete the weapon handling course",
        "Learn time manipulation mechanics",
        "Neutralize all training targets",
        "Complete in under 3 minutes"
      ],
      description: "Welcome to SWAT team training. This introductory mission familiarizes you with the core mechanics of Time Shooter 3 Unblocked, including weapon handling, movement, and the signature time-slowing ability. Master these basics before taking on real operations."
    },
    {
      id: 2,
      title: "Bank Heist Response",
      difficulty: "Easy",
      objectives: [
        "Neutralize all armed robbers",
        "Rescue at least 5 hostages",
        "Defuse the explosive device",
        "Secure the vault area"
      ],
      description: "Armed robbers have taken over Downtown National Bank with hostages inside. Your SWAT team must breach the building, eliminate the threats, and rescue the civilians. Intelligence suggests explosives may be present, so proceed with caution."
    },
    {
      id: 3,
      title: "Harbor Infiltration",
      difficulty: "Medium",
      objectives: [
        "Intercept the weapons shipment",
        "Secure evidence of illegal operations",
        "Neutralize all hostile targets",
        "Extract safely from the dock area"
      ],
      description: "An illegal weapons shipment is arriving at the harbor tonight. Your mission is to infiltrate the dockyard, eliminate the armed guards, and secure the contraband. Expect resistance from well-armed mercenaries protecting their valuable cargo."
    },
    {
      id: 4,
      title: "High-rise Hostage Situation",
      difficulty: "Hard",
      objectives: [
        "Rescue the VIP hostage",
        "Neutralize terrorist threats",
        "Defuse bombs on multiple floors",
        "Secure the building"
      ],
      description: "Terrorists have seized control of a corporate skyscraper and are threatening to execute hostages, including a high-value government official. You must ascend the building floor by floor, clearing threats and disarming explosive devices before time runs out."
    },
    {
      id: 5,
      title: "Subway Crisis",
      difficulty: "Medium",
      objectives: [
        "Stop the runaway train",
        "Rescue civilians trapped in subway cars",
        "Neutralize terrorist operatives",
        "Prevent the detonation of explosives"
      ],
      description: "Terrorists have hijacked a subway train and planted explosives throughout the underground system. Your team must board the moving train, eliminate the hijackers, and prevent a catastrophic explosion in the heart of the city."
    },
    {
      id: 6,
      title: "Chemical Plant Breach",
      difficulty: "Expert",
      objectives: [
        "Prevent the release of toxic chemicals",
        "Rescue plant workers",
        "Eliminate all hostile forces",
        "Secure critical infrastructure points"
      ],
      description: "A terrorist cell has seized control of a chemical processing facility and threatens to release deadly toxins into the city's water supply. You must navigate the hazardous environment, rescue trapped workers, and neutralize the threat before environmental catastrophe strikes."
    }
  ];

  // SVG for difficulty indicator
  const DifficultyBadge = ({ level }) => {
    const getColor = () => {
      switch(level) {
        case "Tutorial": return "bg-blue-500";
        case "Easy": return "bg-green-500";
        case "Medium": return "bg-yellow-500";
        case "Hard": return "bg-orange-500";
        case "Expert": return "bg-red-500";
        default: return "bg-gray-500";
      }
    };
    
    return (
      <span className={`${getColor()} text-white text-xs font-medium px-2.5 py-0.5 rounded`}>
        {level}
      </span>
    );
  };

  // SVG mission icon
  const MissionIcon = (id) => {
    const iconPatterns = [
      // Bank
      <svg key="bank" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
        <rect x="5" y="15" width="30" height="20" rx="1" fill="#1A1F2C" stroke="#7E69AB" strokeWidth="1"/>
        <polygon points="20,5 35,15 5,15" fill="#9b87f5"/>
        <rect x="10" y="20" width="5" height="15" fill="#6E59A5"/>
        <rect x="18" y="20" width="5" height="15" fill="#6E59A5"/>
        <rect x="25" y="20" width="5" height="15" fill="#6E59A5"/>
      </svg>,
      
      // Training
      <svg key="training" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
        <rect x="10" y="10" width="20" height="20" rx="2" fill="#1A1F2C" stroke="#7E69AB" strokeWidth="1"/>
        <circle cx="20" cy="20" r="8" fill="none" stroke="#9b87f5" strokeWidth="1"/>
        <circle cx="20" cy="20" r="5" fill="none" stroke="#9b87f5" strokeWidth="1"/>
        <circle cx="20" cy="20" r="2" fill="#9b87f5"/>
      </svg>,
      
      // Harbor
      <svg key="harbor" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
        <path d="M5 20 L35 20 L35 30 L5 30 Z" fill="#1A1F2C" stroke="#7E69AB" strokeWidth="1"/>
        <rect x="10" y="10" width="8" height="10" fill="#6E59A5"/>
        <rect x="22" y="15" width="8" height="5" fill="#6E59A5"/>
        <path d="M15 10 L15 5 L20 5 L20 15" fill="none" stroke="#9b87f5" strokeWidth="1"/>
      </svg>,
      
      // Skyscraper
      <svg key="skyscraper" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
        <rect x="10" y="5" width="20" height="30" rx="1" fill="#1A1F2C" stroke="#7E69AB" strokeWidth="1"/>
        <rect x="13" y="8" width="4" height="4" fill="#9b87f5"/>
        <rect x="23" y="8" width="4" height="4" fill="#9b87f5"/>
        <rect x="13" y="15" width="4" height="4" fill="#9b87f5"/>
        <rect x="23" y="15" width="4" height="4" fill="#9b87f5"/>
        <rect x="13" y="22" width="4" height="4" fill="#9b87f5"/>
        <rect x="23" y="22" width="4" height="4" fill="#9b87f5"/>
        <rect x="18" y="28" width="4" height="7" fill="#6E59A5"/>
      </svg>,
      
      // Subway
      <svg key="subway" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
        <rect x="5" y="20" width="30" height="10" rx="3" fill="#1A1F2C" stroke="#7E69AB" strokeWidth="1"/>
        <circle cx="10" cy="32" r="3" fill="#9b87f5"/>
        <circle cx="30" cy="32" r="3" fill="#9b87f5"/>
        <rect x="8" y="22" width="5" height="5" fill="#6E59A5"/>
        <rect x="18" y="22" width="5" height="5" fill="#6E59A5"/>
        <rect x="28" y="22" width="5" height="5" fill="#6E59A5"/>
      </svg>,
      
      // Chemical Plant
      <svg key="chemical" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
        <path d="M15 10 L25 10 L25 20 L30 30 L10 30 L15 20 Z" fill="#1A1F2C" stroke="#7E69AB" strokeWidth="1"/>
        <circle cx="20" cy="15" r="3" fill="#9b87f5"/>
        <path d="M15 25 L25 25" stroke="#6E59A5" strokeWidth="1"/>
      </svg>
    ];
    
    // Return icon based on mission id, modulo to cycle through available icons
    return iconPatterns[(id - 1) % iconPatterns.length];
  };

  return (
    <section className="my-16" id="missions">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold mb-4 text-center text-purple-300">Mission Briefings</h2>
        <p className="text-lg text-center mb-10 text-indigo-100">
          Time Shooter 3 Unblocked features a variety of challenging missions, each with unique objectives and environments.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {missions.map((mission) => (
            <div key={mission.id} className="bg-indigo-900/30 rounded-lg overflow-hidden border border-indigo-800/50 hover:border-purple-500/50 transition-colors duration-300">
              <div className="bg-indigo-800/50 p-4 flex items-center gap-4">
                <div className="bg-purple-900/70 p-2 rounded-md flex items-center justify-center">
                  {MissionIcon(mission.id)}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-bold text-purple-300">{mission.title}</h3>
                    <DifficultyBadge level={mission.difficulty} />
                  </div>
                  <div className="flex items-center mt-1">
                    <span className="text-xs text-indigo-300 border border-indigo-700 rounded px-2 py-0.5">Mission {mission.id}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <p className="mb-4 text-indigo-100">{mission.description}</p>
                
                <div>
                  <h4 className="font-semibold text-purple-300 mb-2">Mission Objectives:</h4>
                  <ul className="space-y-1 list-inside list-disc text-indigo-100">
                    {mission.objectives.map((objective, index) => (
                      <li key={index}>{objective}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4 text-center text-purple-300">Mission Progression</h3>
          <p className="text-lg mb-6 text-center text-indigo-100">
            Complete missions to unlock new weapons, equipment, and even more challenging scenarios. Time Shooter 3 Unblocked features a progressive difficulty system that adapts to your skill level.
          </p>
          
          {/* SVG progression chart */}
          <div className="flex justify-center mb-6">
            <svg viewBox="0 0 400 80" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-2xl">
              <line x1="50" y1="40" x2="350" y2="40" stroke="#6E59A5" strokeWidth="2" strokeDasharray="5,5" />
              
              <circle cx="50" cy="40" r="10" fill="#9b87f5" />
              <text x="50" y="65" fontSize="12" fill="#D6BCFA" textAnchor="middle">Training</text>
              
              <circle cx="125" cy="40" r="10" fill="#8B5CF6" />
              <text x="125" y="65" fontSize="12" fill="#D6BCFA" textAnchor="middle">Easy</text>
              
              <circle cx="200" cy="40" r="10" fill="#7E69AB" />
              <text x="200" y="65" fontSize="12" fill="#D6BCFA" textAnchor="middle">Medium</text>
              
              <circle cx="275" cy="40" r="10" fill="#6E59A5" stroke="#9b87f5" strokeWidth="1"/>
              <text x="275" y="65" fontSize="12" fill="#D6BCFA" textAnchor="middle">Hard</text>
              
              <circle cx="350" cy="40" r="10" fill="#1A1F2C" stroke="#9b87f5" strokeWidth="2"/>
              <text x="350" y="65" fontSize="12" fill="#D6BCFA" textAnchor="middle">Expert</text>
              
              <path d="M60 40 L115 40" stroke="#9b87f5" strokeWidth="2" />
              <path d="M135 40 L190 40" stroke="#8B5CF6" strokeWidth="2" />
              <path d="M210 40 L265 40" stroke="#7E69AB" strokeWidth="2" />
              <path d="M285 40 L340 40" stroke="#6E59A5" strokeWidth="1" strokeDasharray="3,3" />
            </svg>
          </div>
          
          <div className="flex justify-center">
            <a href="#play-now" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all transform hover:scale-105">
              Start Your First Mission
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameMissions;
